import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Search, X, Clock } from 'lucide-react';
import { api } from '../api/client';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface HelpVideo {
  id: string;
  title: string;
  description: string;
  video_url: string;
  thumbnail_url: string | null;
  duration_seconds: number;
  duration_display: string;
  sort_order: number;
}

interface HelpCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  sort_order: number;
  video_count: number;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/* ------------------------------------------------------------------ */
/*  Video Card                                                         */
/* ------------------------------------------------------------------ */

function VideoCard({ video, onWatch }: { video: HelpVideo; onWatch: () => void }) {
  return (
    <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl overflow-hidden flex flex-col">
      {/* Thumbnail */}
      <button onClick={onWatch} className="relative w-full aspect-video bg-fieldiq-dark flex items-center justify-center group">
        {video.thumbnail_url ? (
          <img
            src={video.thumbnail_url}
            alt={video.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-fieldiq-border to-fieldiq-dark flex items-center justify-center">
            <Play size={40} className="text-muted-foreground group-hover:text-fieldiq-accent transition-colors" />
          </div>
        )}
        {/* Play overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-fieldiq-accent/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Play size={20} className="text-black ml-0.5" fill="currentColor" />
          </div>
        </div>
        {/* Duration badge */}
        {video.duration_seconds > 0 && (
          <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded font-medium">
            {video.duration_display || formatDuration(video.duration_seconds)}
          </span>
        )}
      </button>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-sm font-semibold text-foreground leading-tight">{video.title}</h3>
        {video.description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{video.description}</p>
        )}
        <div className="mt-auto pt-3">
          <button
            onClick={onWatch}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-fieldiq-accent text-black hover:bg-fieldiq-accent/90 transition-colors"
          >
            <Play size={14} fill="currentColor" /> Watch
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Video Player Modal                                                 */
/* ------------------------------------------------------------------ */

function VideoPlayerModal({ video, onClose }: { video: HelpVideo; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-50 bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
        >
          <X size={18} />
        </button>

        {/* Video player */}
        <video
          src={video.video_url}
          controls
          autoPlay
          className="w-full aspect-video bg-black"
        />

        {/* Info below player */}
        <div className="p-5">
          <h2 className="text-lg font-bold text-foreground">{video.title}</h2>
          {video.duration_seconds > 0 && (
            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
              <Clock size={12} />
              {video.duration_display || formatDuration(video.duration_seconds)}
            </div>
          )}
          {video.description && (
            <p className="text-sm text-muted-foreground mt-3 whitespace-pre-line">{video.description}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */

export default function HelpVideosScreen() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [watchingVideo, setWatchingVideo] = useState<HelpVideo | null>(null);

  // Fetch categories
  const { data: categoriesData, isLoading: loadingCategories } = useQuery({
    queryKey: ['help-video-categories'],
    queryFn: () => api.helpVideos.categories().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const categories: HelpCategory[] = categoriesData?.rows ?? [];

  // Set first category as active once loaded
  const activeCategorySlug = activeCategory ?? categories[0]?.slug ?? null;

  // Fetch videos for active category
  const { data: categoryData, isLoading: loadingVideos } = useQuery({
    queryKey: ['help-videos-category', activeCategorySlug],
    queryFn: () =>
      api.helpVideos.categoryBySlug(activeCategorySlug!).then((r) => r.data),
    enabled: !!activeCategorySlug,
    staleTime: 5 * 60 * 1000,
  });

  const videos: HelpVideo[] = categoryData?.videos ?? [];

  // Filter videos by search
  const filteredVideos = useMemo(() => {
    if (!search.trim()) return videos;
    const q = search.toLowerCase();
    return videos.filter(
      (v) =>
        v.title.toLowerCase().includes(q) ||
        (v.description && v.description.toLowerCase().includes(q))
    );
  }, [videos, search]);

  const isLoading = loadingCategories || loadingVideos;

  return (
    <div className="min-h-full pb-[var(--bottom-chrome)] bg-background">
      {/* Action bar */}
      <div className="border-b border-border px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors"
        >
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="font-bold text-2xl text-foreground">Help & Training Videos</h1>
        </div>
      </div>

      {/* Search bar */}
      <div className="px-6 py-4">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search videos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-fieldiq-dark border border-fieldiq-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-fieldiq-accent/50 transition-colors"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Category tabs */}
      {categories.length > 0 && (
        <div className="border-b border-border px-6 flex items-center gap-6 overflow-x-auto">
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => { setActiveCategory(cat.slug); setSearch(''); }}
              className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeCategorySlug === cat.slug
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {cat.name}
              {cat.video_count > 0 && (
                <span className="ml-1.5 text-xs text-muted-foreground">({cat.video_count})</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="px-6 py-4 space-y-4">
        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Loading videos...</div>
        ) : filteredVideos.length === 0 ? (
          <div className="text-center py-12">
            <Play size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-sm">
              {search
                ? 'No videos match your search.'
                : 'No videos yet \u2014 check back soon.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredVideos.map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                onWatch={() => setWatchingVideo(video)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Video player modal */}
      {watchingVideo && (
        <VideoPlayerModal
          video={watchingVideo}
          onClose={() => setWatchingVideo(null)}
        />
      )}
    </div>
  );
}
